import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAppId } from './AppIdContext';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const BulkSlipWtspContext = createContext(null);

export const useBulkSlipWtsp = () => {
    const context = useContext(BulkSlipWtspContext);
    if (!context) {
        throw new Error('useBulkSlipWtsp must be used within a BulkSlipWtspProvider');
    }
    return context;
};

export const BulkSlipWtspProvider = ({ children }) => {

    const { aacharSahita } = useAppId();

    // campaign = { boothNo, voters, totalVoters, sentCount, previewIndex, isSending }
    const [campaign, setCampaign] = useState(null);
    const abortControllerRef = useRef(null);

    // ✅ ADDED achar_shahita in the parameters
    const startBulkSend = useCallback(async ({ boothNo, voters, userId, appId, wtspDeviceId, userName, achar_shahita }) => {
        if (!voters || voters.length === 0) return;

        const formattedVoters = [];

        // 1. Fetch Single Banner URL ONCE (not per voter)
        let dynamicBannerUrl = ""; // No default fallback

        try {
            const firstVoterId = String(voters[0]?.id || "1");
            const bannerRes = await axios.post(`${BASE_URL}/api/get_family_slip_photo_with_log`, {
                app_id: appId,
                voter_id: firstVoterId,
                user_id: userId
            });

            console.log("Banner API response:", bannerRes.data);

            if (bannerRes?.data?.success) {
                const resultArr = bannerRes.data?.data?.result;
                if (Array.isArray(resultArr) && resultArr.length > 0 && resultArr[0]?.img) {
                    dynamicBannerUrl = resultArr[0].img;
                }
            }
        } catch (error) {
            console.error("Error fetching banner URL:", error?.response?.data || error.message);
        }

        console.log("Final single bannerUrl for all voters:", dynamicBannerUrl);

        // 2. Loop through voters to format details (NO API call inside loop now)
        for (const v of voters) {
            const fullName = [v.eng_f_name, v.eng_m_name, v.eng_surname].filter(Boolean).join(' ');
            let genderAge = v.sex_age;

            let mobile = v.contact_no ? String(v.contact_no).replace(/\D/g, '') : "";
            if (mobile.length === 10) {
                mobile = "91" + mobile;
            }

            formattedVoters.push({
                "partNo": String(v.part_no || ""),
                "boothNo": String(boothNo || v.part_no || ""),
                "serialNo": String(v.slnoinpart || ""),
                "name": fullName,
                "idCard": v.idcard_no || "",
                "address": v.eng_polling_location || v.eng_localityid || "",
                "mobile": mobile
            });
        }

        // ✅ NEW: Achar Shahita Logic
        let finalBannerUrl = dynamicBannerUrl;
        let finalAgentName = userName || "";
        let finalCompanyName = "HL Group Of Companies";

        // જો આચાર સંહિતા લાગુ હોય તો સ્પેસ (" ") મોકલીશું 
        // જેથી બેકએન્ડના ડિફોલ્ટ નામ ઓવરરાઈડ થઈ જાય અને સ્લિપમાં ખાલી જગ્યા આવે.
        
        console.log(Number(aacharSahita));
        
        // String conversion is used to be safe
        if (Number(aacharSahita) === 1) {
            finalBannerUrl = "";
            finalAgentName = "";
            finalCompanyName = "";
        }

        // Prepare final Payload for Stream API
        const payload = {
            deviceId: wtspDeviceId ? String(wtspDeviceId) : "",
            bannerUrl: finalBannerUrl,
            agentName: finalAgentName,
            companyName: finalCompanyName,
            customEndpoint: "https://hlele.bjpwings.com/InsLogHandler.ashx",
            appId: appId ? String(appId) : "1",
            userId: userId ? String(userId) : "123456",
            voters: formattedVoters
        };

        setCampaign({
            boothNo,
            voters,
            totalVoters: voters.length,
            sentCount: 0,
            previewIndex: 0,
            isSending: true
        });

        abortControllerRef.current = new AbortController();

        try {
            const response = await fetch("https://whatsapp.hlgroups.in/api/send-voter-slips-stream", {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': "my_secret_key_123"
                },
                body: JSON.stringify(payload),
                signal: abortControllerRef.current.signal
            });

            if (!response.body) throw new Error("ReadableStream not supported in this browser.");

            const reader = response.body.getReader();
            const decoder = new TextDecoder("utf-8");

            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    setCampaign(prev => prev ? { ...prev, isSending: false } : prev);
                    break;
                }

                const chunk = decoder.decode(value, { stream: true });
                const updates = chunk.split('\n').filter(line => line.trim() !== '');
                const updateCount = updates.length > 0 ? updates.length : 1;

                setCampaign(prev => {
                    if (!prev) return prev;
                    const nextCount = Math.min(prev.sentCount + updateCount, prev.totalVoters);
                    const nextIndex = prev.previewIndex + updateCount < prev.voters.length
                        ? prev.previewIndex + updateCount
                        : prev.previewIndex;
                    return { ...prev, sentCount: nextCount, previewIndex: nextIndex };
                });
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('WhatsApp broadcast streaming stopped by user.');
            } else {
                console.error("WhatsApp Bulk Send Streaming Error:", error);
            }
            setCampaign(prev => prev ? { ...prev, isSending: false } : prev);
        }
    }, [aacharSahita]); // ✅ FIXED: Added aacharSahita to the dependency array

    const stopBulkSend = useCallback(async (wtspDeviceId) => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        setCampaign(prev => prev ? { ...prev, isSending: false } : prev);

        try {
            await axios.post("https://whatsapp.hlgroups.in/api/stop-stream", {
                deviceId: wtspDeviceId ? String(wtspDeviceId) : ""
            }, {
                headers: {
                    'x-api-key': "my_secret_key_123"
                }
            });
            console.log("WhatsApp broadcast stopped on server successfully.");
        } catch (error) {
            console.error("Error stopping WhatsApp broadcast on server:", error);
        }
    }, []);

    const clearCampaign = useCallback(() => setCampaign(null), []);

    return (
        <BulkSlipWtspContext.Provider value={{ campaign, startBulkSend, stopBulkSend, clearCampaign }}>
            {children}
        </BulkSlipWtspContext.Provider>
    );
};